import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Component, ReactNode } from 'react';
import { getSubscriptionHistoryEvaluation } from '../../../api/users';
import {
  PurchaseHistories,
  SubscriptionEvaluation,
  SubscriptionHistoryItem,
} from '../../../api/users.interface';

const PLATFORM_DISPLAY: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  stripe: 'Stripe',
};

export const MEMBERSHIP_LEVELS: Record<string, Record<string, string>> = {
  ios: { '001': 'Monthly', '002': '3-Month', '003': 'Yearly' },
  android: { '01': 'Monthly', '002': '3-Month', '003': 'Yearly' },
  stripe: { '001': 'Monthly', '002': '3-Month', '003': 'Yearly' },
};

interface ComponentProps {
  uid: string;
  purchaseHistories: PurchaseHistories;
}

interface ComponentState {
  loading: boolean;
  evaluation?: SubscriptionEvaluation;
  error?: string;
  selectedItemKey?: string;
}

class PurchaseHistoriesComponent extends Component<
  ComponentProps,
  ComponentState
> {
  private mounted = false;

  state: ComponentState = {
    loading: true,
  };

  componentDidMount() {
    this.mounted = true;
    void this.loadEvaluation();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  loadEvaluation = async () => {
    this.setState({ loading: true, error: undefined });
    try {
      const evaluation = await getSubscriptionHistoryEvaluation(this.props.uid);
      if (this.mounted) {
        this.setState({ evaluation, loading: false });
      }
    } catch (error) {
      if (this.mounted) {
        this.setState({
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Unable to evaluate subscription history.',
        });
      }
    }
  };

  openDetails = (key: string) => {
    this.setState({ selectedItemKey: key });
  };

  closeDetails = () => {
    this.setState({ selectedItemKey: undefined });
  };

  renderSummary() {
    const { evaluation, loading, error } = this.state;

    if (loading && !evaluation) {
      return (
        <Box display="flex" alignItems="center" gap={1.5} py={2}>
          <CircularProgress size={24} />
          <Typography>
            Checking the subscription the app would recognize…
          </Typography>
        </Box>
      );
    }

    if (!evaluation) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={this.loadEvaluation}>
              Retry
            </Button>
          }
        >
          {error || 'Unable to evaluate subscription history.'}
        </Alert>
      );
    }

    const unavailable = evaluation.validation === 'unavailable';
    return (
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={1}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Subscription history
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Checked {formatCheckedDateTime(evaluation.checkedAt)}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={this.loadEvaluation}
            disabled={loading}
          >
            {loading ? 'Checking…' : 'Retry check'}
          </Button>
        </Stack>
        {unavailable && (
          <Alert severity="warning">
            The app treats this result as inactive because live verification was
            unavailable.
            {evaluation.validationMessage
              ? ` ${evaluation.validationMessage}`
              : ''}
          </Alert>
        )}
        {!unavailable && evaluation.validationMessage && (
          <Alert severity="warning">{evaluation.validationMessage}</Alert>
        )}
        {error && (
          <Alert severity="error">The latest retry failed: {error}</Alert>
        )}
      </Stack>
    );
  }

  renderPurchaseCard(item: SubscriptionHistoryItem) {
    const status = this.statusForItem(item);

    return (
      <Grid item xs={12} sm={6} md={4} key={item.key}>
        <Card
          variant={item.active ? 'outlined' : undefined}
          sx={{
            height: '100%',
            borderColor: item.active ? 'success.main' : undefined,
            boxShadow: item.active ? 3 : undefined,
          }}
        >
          <CardActionArea
            onClick={() => this.openDetails(item.key)}
            aria-label={`View ${item.platformDisplay} ${item.planName} subscription details`}
          >
            <CardHeader
              title={item.platformDisplay}
              subheader={formatDisplayDate(
                item.transactionDateIso || item.transactionDate
              )}
              action={
                <Typography variant="caption" color="primary" mt={1} mr={1}>
                  View details
                </Typography>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              <Typography fontWeight={600}>{item.planName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {item.renewalOrExpiry
                  ? formatRenewalOrExpiry(item.renewalOrExpiry)
                  : 'Renewal or expiry date unavailable'}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                mt={1.5}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip label={status.label} color={status.color} size="small" />
                {item.environment !== 'production' && (
                  <Chip
                    label={formatLabel(item.environment)}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>
    );
  }

  statusForItem(item: SubscriptionHistoryItem) {
    return item.active
      ? { label: 'Active', color: 'success' as const }
      : item.selected && this.state.evaluation?.validation === 'unavailable'
      ? { label: 'Verification unavailable', color: 'warning' as const }
      : item.selected
      ? { label: 'Inactive', color: 'default' as const }
      : { label: 'Historical', color: 'default' as const };
  }

  renderDetailsDialog(item: SubscriptionHistoryItem | undefined) {
    if (!item) {
      return null;
    }

    const status = this.statusForItem(item);
    const detailSections = buildDetailSections(item);
    const source = purchaseSource(item.platform);
    const statusMessage = item.active
      ? 'This is the subscription the app currently recognizes.'
      : item.selected && this.state.evaluation?.validation === 'unavailable'
      ? "We couldn't confirm this subscription with the purchase provider."
      : item.statusReason
      ? item.statusReason
      : item.selected
      ? 'The app does not currently grant access from this subscription.'
      : "This subscription is part of the user's purchase history.";

    return (
      <Dialog
        open
        onClose={this.closeDetails}
        fullWidth
        maxWidth="md"
        aria-labelledby="subscription-details-title"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle id="subscription-details-title" sx={{ p: 3, pb: 2.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: '0.08em' }}
              >
                {source}
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {item.planName} subscription
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Purchased{' '}
                {formatDisplayDate(
                  item.transactionDateIso || item.transactionDate
                )}
              </Typography>
            </Box>
            <IconButton
              onClick={this.closeDetails}
              aria-label="Close subscription details"
              edge="end"
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: item.active
                  ? 'rgba(46, 125, 50, 0.08)'
                  : item.selected &&
                    this.state.evaluation?.validation === 'unavailable'
                  ? 'rgba(237, 108, 2, 0.08)'
                  : 'action.hover',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ sm: 'center' }}
                spacing={1}
              >
                <Chip label={status.label} color={status.color} size="small" />
                <Typography variant="body2">{statusMessage}</Typography>
              </Stack>
            </Box>

            <DetailSection title="Subscription overview">
              <InfoField label="Plan" value={item.planName} />
              <InfoField label="Purchased through" value={source} />
              <InfoField
                label="Purchase date"
                value={formatDisplayDate(
                  item.transactionDateIso || item.transactionDate
                )}
              />
              {item.renewalOrExpiry && (
                <InfoField
                  label={renewalLabel(item.renewalOrExpiry)}
                  value={renewalValue(item.renewalOrExpiry)}
                />
              )}
              {item.environment !== 'production' && (
                <InfoField
                  label="Store environment"
                  value={formatLabel(item.environment)}
                />
              )}
            </DetailSection>

            {detailSections.subscription.length > 0 && (
              <DetailSection title="Purchase details">
                {detailSections.subscription.map(({ label, value }) => (
                  <InfoField
                    key={`${item.key}-${label}`}
                    label={friendlyDetailLabel(label)}
                    value={friendlyDetailValue(label, value)}
                  />
                ))}
              </DetailSection>
            )}

            <DetailSection title="Reference information">
              {item.productId && (
                <InfoField label="Product code" value={item.productId} />
              )}
              {detailSections.reference.map(({ label, value }) => (
                <InfoField
                  key={`${item.key}-${label}`}
                  label={friendlyDetailLabel(label)}
                  value={value}
                />
              ))}
            </DetailSection>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeDetails}>Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const items =
      this.state.evaluation?.items ??
      fallbackItems(this.props.purchaseHistories);
    const selectedItem = items.find(
      ({ key }) => key === this.state.selectedItemKey
    );

    return (
      <Box p={2}>
        {this.renderSummary()}
        {!this.state.loading && items.length === 0 && (
          <Typography color="text.secondary" mt={3}>
            No purchase history is stored for this user.
          </Typography>
        )}
        {items.length > 0 && (
          <Grid container spacing={2} mt={0.5}>
            {items.map((item) => this.renderPurchaseCard(item))}
          </Grid>
        )}
        {this.renderDetailsDialog(selectedItem)}
      </Box>
    );
  }
}

const DetailSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Box>
    <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const InfoField = ({ label, value }: { label: string; value: string }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  </Grid>
);

const HIDDEN_DETAIL_LABELS = new Set([
  'Apple receipt',
  'Purchase token',
  'Product ID',
  'Snapshot status',
  'Live mode',
  'Purchase date',
  'Purchase time',
  'Expires',
  'Current period ends',
  'Currency',
]);

const REFERENCE_DETAIL_LABELS = new Set([
  'Subscription ID',
  'Customer',
  'Order ID',
  'Package',
  'Transaction ID',
  'Original transaction ID',
]);

function buildDetailSections(item: SubscriptionHistoryItem) {
  const visibleDetails = item.details.filter(
    ({ label }) => !HIDDEN_DETAIL_LABELS.has(label)
  );

  return {
    subscription: visibleDetails.filter(
      ({ label }) => !REFERENCE_DETAIL_LABELS.has(label)
    ),
    reference: visibleDetails.filter(({ label }) =>
      REFERENCE_DETAIL_LABELS.has(label)
    ),
  };
}

function friendlyDetailLabel(label: string): string {
  const replacements: Record<string, string> = {
    'Current period starts': 'Current billing period starts',
    'Current period ends': 'Current billing period ends',
    'Cancels at period end': 'Cancellation scheduled',
    'Collection method': 'Payment collection',
    'Purchase state': 'Store purchase',
    Acknowledged: 'Google Play confirmation',
    'Auto-renewing': 'Renews automatically',
    Expires: 'Access ends',
    Cancelled: 'Cancelled on',
    'Trial period': 'Free trial',
    'Intro offer period': 'Introductory offer',
    Package: 'App identifier',
    Customer: 'Customer reference',
    'Subscription ID': 'Subscription reference',
    'Order ID': 'Order reference',
    'Transaction ID': 'Transaction reference',
    'Original transaction ID': 'Original transaction reference',
  };

  return replacements[label] || label;
}

const DATE_DETAIL_LABELS = new Set([
  'Current period starts',
  'Current period ends',
  'Trial starts',
  'Trial ends',
  'Subscription started',
  'Auto-resume date',
  'Purchase date',
  'Original purchase date',
  'Expires',
  'Cancelled',
]);

function friendlyDetailValue(label: string, value: string): string {
  return DATE_DETAIL_LABELS.has(label) ? formatDisplayDate(value) : value;
}

function purchaseSource(platform: string): string {
  if (platform === 'ios') return 'Apple App Store';
  if (platform === 'android') return 'Google Play';
  if (platform === 'stripe') return 'Stripe';
  return PLATFORM_DISPLAY[platform] || formatLabel(platform);
}

function renewalLabel(value: string): string {
  if (value.startsWith('Renews ')) return 'Next renewal';
  if (value.startsWith('Expired ')) return 'Access ended';
  return 'Access through';
}

function renewalValue(value: string): string {
  return formatDisplayDate(value.replace(/^(Renews|Expires|Expired)\s+/, ''));
}

function formatRenewalOrExpiry(value: string): string {
  const match = value.match(/^(Renews|Expires|Expired)\s+(.+)$/);
  return match ? `${match[1]} ${formatDisplayDate(match[2])}` : value;
}

function fallbackItems(
  purchaseHistories: PurchaseHistories
): SubscriptionHistoryItem[] {
  return Object.entries(purchaseHistories || {})
    .map(([key, purchase]) => {
      const productId = purchase.productID || purchase.productId;
      const platform = purchase.platform || 'unknown';
      return {
        key,
        platform,
        platformDisplay: PLATFORM_DISPLAY[platform] || `Unknown (${platform})`,
        productId,
        planName:
          MEMBERSHIP_LEVELS[platform]?.[productId] ||
          `Unknown (${productId || 'missing'})`,
        transactionDate: purchase.transactionDate || 0,
        environment: purchase.isDevEnvironment
          ? 'development'
          : purchase.isFromTestflight
          ? 'testflight'
          : 'production',
        selected: false,
        active: false,
        details: [
          {
            label: 'Live details',
            value: 'Unavailable until the subscription check succeeds.',
          },
        ],
      };
    })
    .sort((first, second) => second.transactionDate - first.transactionDate);
}

function formatDisplayDate(value: string | number): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Unknown date'
    : new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
}

function formatCheckedDateTime(value: string | number): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Unknown date'
    : new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
}

function formatLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export default PurchaseHistoriesComponent;
